import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    TextField,
    Button,
    Typography,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import { useSetSnackBar } from '../../../store/Snackbar.state';
import { AppCard, Loading } from '../../atoms';
import {
    useAddProductGroup,
    AddProductGroupRequest,
} from '../../../hooks/bank/useAddProductGroup';
import {
    useUpdateProductGroup,
    UpdateProductGroupRequest,
    useGetProductGroupDetail,
    ProductGroupDetail,
} from '../../../hooks';

type Mode = 'add' | 'edit';

interface ProductGroupModalProps {
    open: boolean;
    mode: Mode;
    productGroupId?: number; // required for edit
    onClose: () => void;
    onSuccess?: () => void; // callback after successful add/edit
}

const STATUS_OPTIONS = [
    { value: 'ACTIVE', label: 'Aktif' },
    { value: 'PASSIVE', label: 'Pasif' },
    { value: 'DELETED', label: 'Silinmiş' },
];

export function ProductGroupModal({
    open,
    mode,
    productGroupId,
    onClose,
    onSuccess,
}: ProductGroupModalProps) {
    const setSnackbar = useSetSnackBar();

    const { mutate: addProductGroup, isLoading: isAddLoading } =
        useAddProductGroup();
    const { mutate: updateProductGroup, isLoading: isUpdateLoading } =
        useUpdateProductGroup();
    const { mutate: getProductGroupDetail, isLoading: isDetailLoading } =
        useGetProductGroupDetail();

    const [formData, setFormData] = useState<
        AddProductGroupRequest | UpdateProductGroupRequest
    >({
        id: 0,
        name: '',
        status: 'ACTIVE',
    });

    const [productGroupData, setProductGroupData] =
        useState<ProductGroupDetail | null>(null);
    const [error, setError] = useState('');

    const isLoading = isAddLoading || isUpdateLoading || isDetailLoading;

    // 🔹 Load product group details if edit mode
    useEffect(() => {
        if (mode === 'edit' && productGroupId) {
            getProductGroupDetail(productGroupId, {
                onSuccess: (data: any) => {
                    if (data?.isSuccess) {
                        const pg = data.data;
                        setProductGroupData(pg);
                        setFormData({
                            id: pg.id,
                            name: pg.name,
                            status: pg.status,
                        });
                    } else {
                        setSnackbar({
                            severity: 'error',
                            description: data?.message || 'Ürün grubu bilgileri alınamadı',
                            isOpen: true,
                        });
                        onClose();
                    }
                },
                onError: () => {
                    setSnackbar({
                        severity: 'error',
                        description: 'Ürün grubu bilgileri alınırken hata oluştu',
                        isOpen: true,
                    });
                    onClose();
                },
            });
        } else if (mode === 'add') {
            setFormData({
                name: '',
                status: 'ACTIVE',
            });
            setProductGroupData(null);
        }
    }, [mode, productGroupId, getProductGroupDetail, setSnackbar, onClose]);

    const handleInputChange =
        (field: keyof typeof formData) => (event: React.ChangeEvent<HTMLInputElement>) => {
            setFormData((prev) => ({
                ...prev,
                [field]: event.target.value,
            }));
            setError('');
        };

    const handleSelectChange =
        (field: keyof typeof formData) => (event: any) => {
            setFormData((prev) => ({
                ...prev,
                [field]: event.target.value,
            }));
            setError('');
        };

    const handleSubmit = () => {
        setError('');

        if (!formData.name.trim()) {
            setError('Grup adı gereklidir');
            return;
        }

        if (mode === 'add') {
            addProductGroup(formData as AddProductGroupRequest, {
                onSuccess: (data: any) => {
                    if (data?.isSuccess) {
                        setSnackbar({
                            severity: 'success',
                            isOpen: true,
                            description: data.message || 'Ürün grubu başarıyla eklendi',
                        });
                        onSuccess?.();
                        onClose();
                    } else {
                        setSnackbar({
                            severity: 'error',
                            description: data?.message || 'Ekleme sırasında hata oluştu',
                            isOpen: true,
                        });
                    }
                },
                onError: () => {
                    setSnackbar({
                        severity: 'error',
                        description: 'Ekleme sırasında hata oluştu',
                        isOpen: true,
                    });
                },
            });
        } else {
            updateProductGroup(formData as UpdateProductGroupRequest, {
                onSuccess: (data: any) => {
                    if (data?.isSuccess) {
                        setSnackbar({
                            severity: 'success',
                            isOpen: true,
                            description: data.message || 'Ürün grubu başarıyla güncellendi',
                        });
                        onSuccess?.();
                        onClose();
                    } else {
                        setSnackbar({
                            severity: 'error',
                            description: data?.message || 'Güncelleme sırasında hata oluştu',
                            isOpen: true,
                        });
                    }
                },
                onError: () => {
                    setSnackbar({
                        severity: 'error',
                        description: 'Güncelleme sırasında hata oluştu',
                        isOpen: true,
                    });
                },
            });
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>
                {mode === 'add' ? 'Yeni Ürün Grubu Ekle' : 'Ürün Grubu Düzenle'}
            </DialogTitle>
            <DialogContent>
                {isLoading ? (
                    <Loading />
                ) : (
                    <AppCard>
                        <CardContent>
                            {error && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {error}
                                </Alert>
                            )}

                            <Grid container spacing={2}>

                                <Grid item xs={12} md={12}>
                                    <TextField
                                        fullWidth
                                        label="Grup Adı"
                                        value={formData.name}
                                        onChange={handleInputChange('name')}
                                        required
                                        variant="outlined"
                                    />
                                </Grid>

                                {mode === 'edit' && productGroupData && (
                                    <>

                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Oluşturma Tarihi"
                                                value={new Date(
                                                    productGroupData.createDate
                                                ).toLocaleString('tr-TR')}
                                                disabled
                                                variant="outlined"
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Güncelleme Tarihi"
                                                value={
                                                    productGroupData.updateDate
                                                        ? new Date(
                                                            productGroupData.updateDate
                                                        ).toLocaleString('tr-TR')
                                                        : 'Henüz güncellenmemiş'
                                                }
                                                disabled
                                                variant="outlined"
                                            />
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                        </CardContent>
                    </AppCard>
                )}
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={onClose} disabled={isLoading}>
                    İptal
                </Button>
                <Button variant="contained" onClick={handleSubmit} disabled={isLoading}>
                    Kaydet
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default ProductGroupModal;
